/**
 * Abilities module public API: movement abilities layered on top of the player motor (air jump,
 * flap jump, dash, skate, grapple, swim, climb, rail), each one a MotorContributor with its own
 * 'abilities.<id>.enabled' setting (all enabled by default) and tuning settings in the 'abilities'
 * settings category. The 'testing' menu page and the testing kiosk toggle them.
 *
 * Bindings (see platform/api.ts): VR A = jump/air jump · B = dash · Y = skate toggle (X free, VR_FREE_BUTTON) · LEFT / RIGHT
 * trigger = that hand's grapple (aimed with that hand; both ropes at once; a hand holding a trigger
 * item uses it instead) · grip on a climbable = climb · flap = both arms swept together (down, back or
 * sideways: the body goes the opposite way, like a bird); desktop Space · Shift ·
 * C · F right rope / Z left rope (camera aim) · E on a climbable (hold to climb) · E under a ceiling rail
 * (hold to hang, forward / back travels along it: the 'rail' ability, engine 0.5 §2.15; phone Grab, pad X).
 *
 * (API guide « Movement »): Game Packs register their own abilities (`AbilityDef`, ids `<id>.*`) and each
 * world applies an `AbilityPreset` through the settings 'world' layer (owner 'abilities:<world>'),
 * so the player's own preferences are never touched and come back in the hub.
 */
import type { GameKey, Handedness } from '@jungle/shared';
import type { Object3D, Vector3 } from 'three';
import type { MotorContributor } from '../player/api';
import type { SettingInput, SettingValue } from '../settings/registry';
import { FlapStrokeDetector } from './flapStroke';

/** Bird-flap math (the flap jump's), for games that want wings of their own. */
export { flapDirection, flapStroke } from './flapStroke';

/** Built-in ability ids (settings keys are 'abilities.<id>.*'). */
export type CoreAbilityId = 'airJump' | 'flap' | 'dash' | 'skate' | 'grapple' | 'swim' | 'climb' | 'rail';

/** A core ability or one registered by a Game Pack ('demo.grind', 'demo.fly'). */
export type AbilityId = CoreAbilityId | GameKey;

/** Every core ability id, in menu order. */
export const ABILITY_IDS: readonly CoreAbilityId[] = ['airJump', 'flap', 'dash', 'skate', 'grapple', 'swim', 'climb', 'rail'];

/** Built-in ability presets of the testing panel. */
export type AbilityPresetId = 'all' | 'none' | 'gt';

/**
 * Value of 'abilities.airJump.count' / 'abilities.flap.count' that means unlimited (the menu shows ∞,
 * `AbilityStatus` reports Infinity). 0 means none. A game sets it only through a host-granted
 * exception (games/movementRule.ts UNLIMITED_MOVEMENT_LAYER).
 */
export const UNLIMITED_COUNT = 99;

/**
 * What `AbilitiesService.refill` restores. Omitted = everything; otherwise only the fields set to
 * true (`{ dash: true }` = the dash alone).
 */
export interface AbilityRefill {
  /** End the dash cooldown: every charge ready. */
  dash?: boolean;
  /** Flaps spent since the last landing come back (up to 'abilities.flap.count'). */
  flap?: boolean;
  /** Air jumps spent since the last landing come back (up to 'abilities.airJump.count'). */
  airJump?: boolean;
}

/** Live state of the local player's abilities (read by the desktop HUD and the wrist watch). */
export interface AbilityStatus {
  /** Air jumps left before touching something again (Infinity when unlimited). */
  airJumpsLeft: number;
  /** Flap jumps left (Infinity when unlimited). */
  flapsLeft: number;
  dashCharges: number;
  dashMaxCharges: number;
  /** 0..1 progress of the next dash charge (1 when every charge is ready). */
  dashRecharge: number;
  dashing: boolean;
  skating: boolean;
  grappleAttached: boolean;
  climbing: boolean;
  /** Body in water with the swim ability active. */
  swimming: boolean;
  /** Head below a water surface. */
  underwater: boolean;
  /**
   * Climb stamina left in seconds (§2.5 'abilities.climb.stamina'); Infinity when that
   * setting is 0 (unlimited, the default). Drained while hanging, refilled on the ground. F1b.
   */
  climbStamina: number;
  /**
   * Hanging from a ceiling rail (flat screens, engine 0.5 §2.15). `climbing` is true then too (the hang
   * counts as a climb: jumps refill, PoseFlags.CLIMBING shows it to others).
   */
  railHanging: boolean;
}

/**
 * How a world treats one ability:
 * - 'on' / 'off';
 * - `{ unlock, else }`: on when the game's resolver says `unlock` is unlocked (save, run state; always
 *   in demo mode), otherwise 'off' (default) or 'hidden' (off and absent from menus / HUD);
 * - `{ on: true, settings }`: on, with world-locked tuning. **Deprecated** (2026-09-26, same movement in
 *   every game: games/movementRule.ts): a Game Pack only turns core abilities on / off, it never
 *   retunes them; use 'on'. No game uses it; kept so the contract stays additive (movementRule.test.ts
 *   refuses a core movement value in its `settings`).
 */
export type AbilityRule =
  | 'on'
  | 'off'
  | { unlock: GameKey; else?: 'off' | 'hidden' }
  | AbilityRuleWithSettings;

/**
 * @deprecated A Game Pack only turns core abilities on / off (same movement in every game,
 * games/movementRule.ts): use 'on'. Kept so the contract stays additive.
 */
export interface AbilityRuleWithSettings {
  on: true;
  settings: Record<string, SettingValue>;
}

/** Per-world ability rules. An ability ABSENT from a preset is off in that world. */
export type AbilityPreset = Partial<Record<AbilityId, AbilityRule>>;

/** Resolved state of one ability in a world. */
export type AbilityState = 'on' | 'off' | 'hidden';

/** An ability registered by a Game Pack (GameScope.abilities.register). */
export interface AbilityDef {
  id: GameKey;
  labelKey: string;
  icon?: string;
  /** Plugs into the player motor like the core abilities. */
  contributor: MotorContributor;
  /** Its tuning (keys `<id>.*`), declared by a data leaf like `settings.define` and defined on first register. */
  settings?: readonly SettingInput[];
  /** Desktop HUD chip + watch line; null hides it this frame. */
  hud?(): { label: string; value?: number } | null;
}

/** A grapple anchor that is not a static collider (an enemy, a boss part). */
export interface GrappleTarget {
  point: Vector3;
  tag: string;
  /** Write the anchor's current world point; false = target lost (the rope releases). */
  follow(out: Vector3): boolean;
}

/** Ray query offered to the grapple for extra targets (enemies without colliders). */
export type GrappleTargetProvider = (origin: Vector3, dir: Vector3, max: number) => GrappleTarget | null;

export interface AbilitiesService {
  readonly status: Readonly<AbilityStatus>;
  isEnabled(id: AbilityId): boolean;
  /** Same as setting 'abilities.<id>.enabled' (turning one off cancels what it is doing). */
  setEnabled(id: AbilityId, on: boolean): void;
  /** Turn skate mode on/off (a watch button may call it; X / C toggle it too). */
  setSkating(on: boolean): void;
  /** Release grapple/climb, end a dash (cutscenes, custom teleports). */
  cancelAll(): void;
  /** 'all' enables every ability, 'none' disables them, 'gt' = Gorilla Tag basics only. */
  applyPreset(id: AbilityPresetId): void;
  /** Reset every 'abilities.*' tuning and toggle to its default. */
  resetSettings(): void;

  // ---- API guide « Movement » --------------------------------------------------------------------
  /** Add a Game Pack ability (its settings are defined once). Returns the unregister function. */
  register(def: AbilityDef): () => void;
  /**
   * null = hub: world layer cleared, preferences intact. Otherwise pushes the world layer (owner
   * 'abilities:<world>') from `resolveAbilityPreset(preset, unlocked)`. Callable again at any time
   * (a mutation unlocked mid-run).
   */
  applyWorldPreset(preset: AbilityPreset | null, unlocked?: (k: GameKey) => boolean): void;
  /** Extra grapple targets queried before the static colliders; returns the remover. */
  addGrappleTarget(provider: GrappleTargetProvider): () => void;
  /** Veto a climb grab (stamina, forbidden holds); null removes the gate. */
  setClimbGate(fn: ((hand: Handedness, tag: string) => boolean) | null): void;
  /** Let go of a climb hold (one hand or both). */
  releaseClimb(hand?: Handedness): void;

  // ---- skate-v2 (2026-09-27) ----------------------------------------------------------------------
  /**
   * Energy crystals (David, 2026-09-26 23:20): end the dash cooldown and give back the
   * flaps and air jumps spent since the last landing, as if the player had just touched the ground.
   * Counts never exceed their current settings (2 each by default); the dash charges go back to
   * 'abilities.dash.charges'. A dash still running keeps running (the refilled charge is ready when
   * it ends), and a refilled dash still never stacks: at most the locomotion's max + one boost, whose
   * excess keeps fading (dashBoost.ts). Call it on a discrete event (a crystal touched), never every
   * frame. Emits 'abilities:refilled' (juice, sound). `what` omitted = all three.
   */
  refill(what?: AbilityRefill): void;

  // ---- kh-abil (2026-10-20) -----------------------------------------------------------------------
  /**
   * Veto the core flap (wings tired, a no-fly zone): asked when a wing stroke is detected; true allows
   * the flap, false vetoes it (no impulse, no flap spent, the stroke detector starts over). A throwing
   * gate counts as allow. null removes the gate. Returns the remover, which clears the gate only while
   * `fn` is still the current one. `applyWorldPreset` (every world start, and mid-run) and the end of
   * the visit clear it: set it after the preset. Optional: a host without it simply has no veto.
   */
  setFlapGate?(fn: (() => boolean) | null): () => void;
  /**
   * Style the grapple ropes, per player and hand (a team tint, a thicker cable, a wavy energy line). The resolver
   * is asked once each time a rope appears: owner 'local' for this player's ropes, the remote's sessionId for
   * another's; null keeps the default rope. A throwing resolver counts as null (one warning). Visual only: the rope
   * physics never changes. At most 4 tinted colours live at once; a 5th wears the default material (one warning).
   * null removes the resolver; the returned remover clears it only while `fn` is still the current one. The end of
   * the visit clears it. Optional: a host without it draws default ropes.
   */
  setRopeStyle?(fn: ((owner: 'local' | string, hand: Handedness) => RopeStyle | null) | null): () => void;
  /**
   * A rope mesh of the engine's own look for a game's use (a tether, a leash, a swinging vine), shown at once. Parent
   * `object` where it belongs, call `update` each frame and `dispose` when done (it frees its geometry and releases
   * its material). Optional: a host without it has none.
   */
  createRope?(style?: RopeStyle): RopeView;
}

/**
 * How a rope looks (visual only). Every field is optional and clamped; a non-finite number takes its default.
 * - `color`: '#rrggbb' tint (a Lambert material of that colour); absent or invalid = the style pack's rope material.
 * - `radius`: tube radius in meters, 0.004-0.04 (default 0.012).
 * - `wave`: a travelling sideways wave, zero at both ends: `amplitude` 0-0.3 m, `wavelength` 0.2-5 m, `speed`
 *   0-10 m/s.
 */
export interface RopeStyle {
  readonly color?: string;
  readonly radius?: number;
  readonly wave?: { readonly amplitude: number; readonly wavelength: number; readonly speed: number };
}

/** A rope mesh (AbilitiesService.createRope): a sagging tube rebuilt in place, no allocation per update. */
export interface RopeView {
  /** The mesh: parent it where the rope belongs. */
  readonly object: Object3D;
  /**
   * Rebuild from `from` to `to` (in the space of `object`'s parent: the mesh stays at its origin), sagging `sag` meters at the middle.
   * `timeS` drives the wave (seconds); omitted, the engine's own clock does.
   */
  update(from: Vector3, to: Vector3, sag: number, timeS?: number): void;
  /** Change the look; null = the default rope. */
  setStyle(s: RopeStyle | null): void;
  setVisible(on: boolean): void;
  /** Remove from the parent, free the geometry and release the material. Idempotent. */
  dispose(): void;
}

/**
 * Resolve a preset into world-layer values: 'abilities.<core>.enabled' for every core ability (absent
 * = off) plus the tuning of `{ on: true, settings }` rules, and the per-ability state (game abilities
 * included). `unlocked` answers `{ unlock }` rules (absent = locked). Pure: shared by F1b and tests.
 */
export function resolveAbilityPreset(
  preset: AbilityPreset,
  unlocked: (k: GameKey) => boolean = () => false,
): { layer: Record<string, SettingValue>; state: Partial<Record<AbilityId, AbilityState>> } {
  const layer: Record<string, SettingValue> = {};
  const state: Partial<Record<AbilityId, AbilityState>> = {};
  const ids = new Set<AbilityId>([...ABILITY_IDS, ...(Object.keys(preset) as AbilityId[])]);
  for (const id of ids) {
    const rule = preset[id];
    let s: AbilityState = 'off';
    if (rule === 'on') s = 'on';
    else if (typeof rule === 'object' && 'unlock' in rule) s = unlocked(rule.unlock) ? 'on' : (rule.else ?? 'off');
    else if (typeof rule === 'object') {
      s = 'on';
      Object.assign(layer, rule.settings);
    }
    state[id] = s;
    if ((ABILITY_IDS as readonly string[]).includes(id)) layer[`abilities.${id}.enabled`] = s === 'on';
  }
  return { layer, state };
}

/** Tuning of a two-hand flap detector (defaults = the 'abilities.flap.*' defaults). */
export interface FlapDetectorOptions {
  /** Hand stroke speed (m/s, positive) that counts as a wing stroke. */
  threshold: number;
  /** Max seconds between the two hands' strokes. */
  window: number;
  /** Seconds after a flap before the next one can register. */
  cooldown: number;
}

/** Two-hand stroke coincidence detector (the flap jump's), for games that want wings of their own. */
export interface FlapDetector {
  readonly options: FlapDetectorOptions;
  /** Feed one frame (rig-relative vertical hand speeds, m/s, +up). True on the frame a flap fires. */
  update(time: number, leftVy: number, rightVy: number): boolean;
  /**
   * Feed one frame with each hand's stroke speed in any direction (m/s, ≥ 0; `flapStroke` gives it,
   * and `flapDirection` the push of the resulting flap). True on the frame a flap fires. The core
   * bird flap uses this one (2026-09-26).
   */
  stroke(time: number, left: number, right: number): boolean;
  reset(): void;
  /** Forget pending strokes (call while a hand pushes on a surface). */
  disarm(): void;
}

/** A new flap detector (e.g. a game's wings power-up). */
export function createFlapDetector(opts: Partial<FlapDetectorOptions> = {}): FlapDetector {
  const options: FlapDetectorOptions = { threshold: 2.2, window: 0.12, cooldown: 0.25, ...opts };
  const core = new FlapStrokeDetector();
  return {
    options,
    update: (time, leftVy, rightVy) => core.update(time, leftVy, rightVy, options),
    stroke: (time, left, right) => core.stroke(time, left, right, options),
    reset: () => core.reset(),
    disarm: () => core.disarm(),
  };
}

declare module '../core/api' {
  interface GameEvents {
    /** An ability was used by the local player (air jump, flap, dash, grapple attach, climb grab…). */
    'abilities:used': { id: AbilityId };
    /** Skate mode toggled (local player). */
    'abilities:skate': { on: boolean };
    /** Dash start / end; `iframesS` = 'abilities.dash.iframes' in effect (INVULNERABLE window). */
    'abilities:dash': { phase: 'start' | 'end'; iframesS: number };
    /**
     * Grapple anchored / released; `tag` = the collider or GrappleTarget tag, `hand` = which of the
     * two ropes (always set by the core; optional only so the event stays additive).
     */
    'abilities:grapple': { phase: 'attach' | 'release'; tag: string; hand?: Handedness };
    /** A hand grabbed / released a climb hold. */
    'abilities:climb': { phase: 'grab' | 'release'; hand: Handedness; tag: string };
    /** `AbilitiesService.refill` ran (an energy crystal): what it restored. */
    'abilities:refilled': { dash: boolean; flap: boolean; airJump: boolean };
  }
}

declare module '../core/api' {
  interface AppServices {
    abilities: AbilitiesService;
  }
}
